/**
 * Platform detection.
 * Per UI-SPEC §1.4 + §9 (PA1-PA5).
 */
export type PlatformName = 'web' | 'pwa' | 'ios' | 'android' | 'telegram' | 'line' | 'discord';

export interface PlatformInfo {
  name: PlatformName;
  isStandalone: boolean;
  prefersReducedMotion: boolean;
  prefersDark: boolean;
  prefersContrast: boolean;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: unknown };
    liff?: unknown;
    DiscordSDK?: unknown;
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }
}

export function detectPlatform(): PlatformInfo {
  const ua = navigator.userAgent;
  let name: PlatformName = 'web';

  if (typeof window !== 'undefined') {
    // Capacitor native shell FIRST — its WKWebView / Android WebView does NOT
    // report PWA signals (navigator.standalone / display-mode: standalone), so
    // the heuristics below mis-detect a native app as plain 'web', leaking
    // web-only UI (the cookie banner) and skipping native init (RevenueCat IAP,
    // streak reminders). Capacitor's own API is the authoritative signal.
    const cap = window.Capacitor;
    const capPlatform = cap?.isNativePlatform?.() ? cap.getPlatform?.() : undefined;
    if (capPlatform === 'ios') name = 'ios';
    else if (capPlatform === 'android') name = 'android';
    else if (window.Telegram?.WebApp) name = 'telegram';
    // window.liff only exists after our own SDK inject; the UA marker is what
    // identifies LINE's in-app browser on first load (LINE MINI App endpoint).
    else if (window.liff || /\bLine\//.test(ua)) name = 'line';
    else if (window.DiscordSDK) name = 'discord';
    else if (
      /(iPhone|iPad|iPod)/i.test(ua) &&
      (window.navigator as { standalone?: boolean }).standalone
    )
      name = 'ios';
    else if (/Android/i.test(ua) && window.matchMedia('(display-mode: standalone)').matches)
      name = 'android';
    else if (window.matchMedia('(display-mode: standalone)').matches) name = 'pwa';
  }

  return {
    name,
    isStandalone: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
    prefersReducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    prefersDark: window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
    prefersContrast: window.matchMedia?.('(prefers-contrast: more)').matches ?? false,
  };
}
